import { Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { CommonModule } from '@angular/common';
import { Store } from '@ngxs/store';
import { GlobalMetadataFactoryService } from '../../../global-metadata/services/global-metadata.factory.service';
import { ObjectTypeEntityService } from '../../../core/global-enums/object-type/services/object-type.entity.service';
import { from, Observable, switchMap, tap } from 'rxjs';
import { ObjectTypeEntity } from '../../../core/global-enums/object-type/object-type.entity';
import { MatTab, MatTabGroup } from '@angular/material/tabs';
import {
  GlobalMetadataSelectComponent
} from '../../../global-metadata/global-metadata-select/global-metadata-select.component';
import { SavedLearnObjectType } from '../../consumer.entity';

@Component({
  selector: 'pixl-consumer-object-type-metadata',
  templateUrl: './consumer-object-type-metadata.component.html',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatTabGroup,
    MatTab,
    GlobalMetadataSelectComponent,
  ],
  standalone: true,
  styleUrls: ['../form-dialog-consumer.component.scss']
})
export class ConsumerObjectTypeMetadataComponent implements OnInit {
  @Input({ required: true }) parentForm!: FormGroup;

  objectTypes$!: Observable<ObjectTypeEntity[]>;

  @Input() savedLearnObjectTypes: SavedLearnObjectType[] = [];

  private store = inject(Store);
  private objectTypeService = inject(ObjectTypeEntityService);
  private metaFactory = inject(GlobalMetadataFactoryService);

  ngOnInit() {
    this.savedLearnObjectTypes = this.parentForm.value.learnObjectTypeDescriptions || [];
    this.loadRelationData();
  }

  private loadRelationData(): void {
    const query = { sort: 'name' };

    this.objectTypes$ = from(
      this.objectTypeService.ensureLoaded(query)
    ).pipe(
      switchMap(() =>
        this.store.select(this.objectTypeService.allByQuery(query))
      ),
      tap(types => this.syncObjectTypeGroups(types))
    );
  }

  private syncObjectTypeGroups(types: ObjectTypeEntity[]) {
    const arr = this.learnObjectTypeDescriptions;

    // Map existing FormGroups by label
    const existing = new Map<string, FormGroup>();
    arr.controls.forEach(ctrl => {
      const label = ctrl.get('label')?.value;
      if (label) existing.set(label, ctrl as FormGroup);
    });

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const savedMap = new Map<string, { label: string, metadata?: any[] }>(
      (this.savedLearnObjectTypes || []).map(t => [t.label, t])
    );

    types.forEach(type => {
      const typeName = type.name;
      if (!typeName) return;

      if (!existing.has(typeName)) {
        const savedType = savedMap.get(typeName);

        const typeGroup = this.metaFactory.createObjectTypeMetadataGroup({
          label: typeName,
          metadata: savedType?.metadata || []
        });

        arr.push(typeGroup);
      }
    });
  }

  getObjectTypeGroup(label: string, createIfMissing = false): FormGroup | null {
    let group = this.learnObjectTypeDescriptions.controls.find(
      c => c.get('label')?.value === label
    ) as FormGroup | undefined;

    if (!group && createIfMissing) {
      // Create empty FormGroup with metadata array
      group = this.metaFactory.createObjectTypeMetadataGroup({ label, metadata: [] });
      this.learnObjectTypeDescriptions.push(group);
    }

    return group ?? null;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public prepareForSave(): any[] {
    return this.learnObjectTypeDescriptions.controls
      .filter((ctrl): ctrl is FormGroup => ctrl instanceof FormGroup)
      .map(group => ({
        label: group.get('label')?.value,
        metadata: (group.get('metadata') as FormArray)?.value || []
      }));
  }

  getSelectedMetadataIds(typeGroup: FormGroup): string[] {
    const metadataArray = typeGroup.get('metadata') as FormArray;
    if (!metadataArray || metadataArray.length === 0) return [];

    return metadataArray.controls
      .filter((ctrl): ctrl is FormGroup => ctrl instanceof FormGroup)
      .map(ctrl => {
        const val = ctrl.value;
        return val._id || val.id; // <-- use the value object
      })
      .filter(id => !!id) as string[];
  }

  get learnObjectTypeDescriptions(): FormArray {
    return this.parentForm.get('learnObjectTypeDescriptions') as FormArray;
  }
}
